<script lang="ts">
  import { Pulse } from "svelte-loading-spinners"
  import { Lock } from "lucide-svelte"
  import { xhr, toast, wait } from "$app/utils"
  import Honeybadger from "$app/utils/honeybadger"
  import { appStore } from "$app/store.svelte"

  let plan_id = $state(0)

  if (appStore.plans.length === 1) {
    plan_id = appStore.plans[0].id
  }

  let form = $state({
    send_daily_updates: null,
    send_weekly_report: null,
    weekly_report_format: null,
    send_push_notifications: appStore.current_user.send_push_notifications,
    send_notification_summary: appStore.current_user.send_notification_summary
  })

  let loadingPlanSettings = $state(false)
  let plan = $state(null)

  async function getPlanUserSettings() {
    loadingPlanSettings = true
    try {
      const resp = await xhr.get(
        `/users/${appStore.current_user.id}/plan_user_settings?plan_id=${plan_id}`
      )
      let settings = resp.data

      form.send_daily_updates = settings.send_daily_updates
      form.send_weekly_report = settings.send_weekly_report
      form.weekly_report_format = settings.weekly_report_format
    } catch (e: any) {
      if (typeof Honeybadger.notify !== "undefined") {
        Honeybadger.notify(e)
      }
      toast("There was an error updating your preferences", "error")
    } finally {
      form = form
      loadingPlanSettings = false
    }
  }

  async function saveGlobalEmailSettings() {
    try {
      await wait(100)
      const resp = await xhr.patch(`/users/${appStore.current_user.id}`, {
        user: {
          send_push_notifications: form.send_push_notifications,
          send_notification_summary: form.send_notification_summary
        }
      })

      toast("Email notification settings have been saved.", "success")
    } catch (e: any) {
      if (typeof Honeybadger.notify !== "undefined") {
        Honeybadger.notify(e)
      }
      toast("There was an error updating your preferences", "error")
    }
  }

  async function savePlanUserSettings() {
    try {
      await wait(100)
      const resp = await xhr.patch(
        `/users/${appStore.current_user.id}/update_plan_user_settings`,
        {
          plan_id: plan_id,
          plan_user_role: {
            send_daily_updates: form.send_daily_updates,
            send_weekly_report: form.send_weekly_report,
            weekly_report_format: form.weekly_report_format
          }
        }
      )
      toast("Email notification settings have been saved.", "success")
    } catch (e: any) {
      if (typeof Honeybadger.notify !== "undefined") {
        Honeybadger.notify(e)
      }
      toast("There was an error updating your preferences", "error")
    }
  }

  $effect(() => {
    if (plan_id !== 0) {
      plan = appStore.plans[0]
      plan_id = plan.id
      getPlanUserSettings()
    }
  })
</script>

<h2
  class="text-lg font-semibold leading-7 text-gray-900 flex flex-col md:flex-row justify-center md:justify-start items-center"
>
  Plan Specific Email Settings
</h2>

{#if appStore.plans.length > 1}
  <div class="flex items-center">
    <label for="plan_id">Plan:</label>
    <select
      bind:value={plan_id}
      onchange={getPlanUserSettings}
      class="md:ml-4 w-64 rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-blue-600 sm:text-sm sm:leading-6"
    >
      {#each appStore.plans as plan (plan.id)}
        <option value={plan.id}>{plan.name}</option>
      {/each}
    </select>
  </div>
{:else}
  <span
    class="mt-2 md:mx-4 border rounded-md border-gray-200 py-2 px-4 flex text-sm items-center bg-gray-50"
  >
    <img
      src={appStore.plans[0].logo_url}
      class="mr-2 w-5 h-5"
      alt={appStore.plans[0].name}
    />
    {appStore.plans[0].name}
  </span>
{/if}

{#if loadingPlanSettings}
  <div class="py-6 flex justify-center w-full">
    <Pulse size="240" color="#cdcdcd" unit="px" duration="1s" />
  </div>
{:else if plan?.subscription?.subscription_type === "free"}
  <div
    class="md:mx-4 mt-4 py-3 px-4 rounded-md bg-yellow-50 border border-yellow-200 text-yellow-800 flex items-center"
  >
    <Lock class="size-6 mr-3 flex-shrink-0" />
    <div class="text-sm">
      Email settings for this plan are only available on our paid plans.
      <a
        href={`/plans/${plan_id}/admin`}
        class="font-medium underline text-yellow-900 hover:text-yellow-800"
        >Upgrade your plan</a
      >
      to access these features.
    </div>
  </div>
{:else}
  <div class="mt-6 grid grid-cols-3 gap-y-2 gap-x-4">
    <div
      class="col-span-2 sm:col-span-1 flex items-center justify-start sm:justify-end"
    >
      <label for="email" class="mt-0 pt-0 text-sm font-medium text-gray-700">
        Send Daily Plan Updates?
      </label>
    </div>
    <div class="flex items-center justify-start col-span-1 sm:col-span-2">
      <div class="max-w-lg flex items-center">
        <label class="cursor-pointer mt-0 pt-0">
          <input
            type="radio"
            name="send_daily_updates"
            id="send-daily-updates-yes"
            bind:group={form.send_daily_updates}
            onchange={savePlanUserSettings}
            value={true}
          />
          <span>Yes</span>
        </label>
        <label class="ml-3 cursor-pointer mt-0 pt-0">
          <input
            type="radio"
            name="send_daily_updates"
            id="send-daily-updates-no"
            bind:group={form.send_daily_updates}
            value={false}
            onchange={savePlanUserSettings}
          />
          <span>No</span>
        </label>
      </div>
    </div>
    <div class="col-span-3 sm:col-start-2 sm:col-span-2">
      <p class="text-gray-500 text-sm">
        Daily summary of activity including recognitions and goal/task updates.
      </p>
    </div>
  </div>
  <div class="mt-6 grid grid-cols-3 gap-y-2 gap-x-4">
    <div
      class="col-span-3 sm:col-span-1 flex items-center justify-start sm:justify-end"
    >
      <label for="email" class="mt-0 pt-0 text-sm font-medium text-gray-700">
        Send Weekly Activity Report?
      </label>
    </div>
    <div class="flex items-center justify-start col-span-3 sm:col-span-2">
      <div class="max-w-lg flex items-center">
        <label class="cursor-pointer mt-0 pt-0">
          <input
            type="radio"
            name="send_weekly_report"
            id="send-weekly-report-yes"
            bind:group={form.send_weekly_report}
            value={true}
            onclick={savePlanUserSettings}
          />
          <span>Yes</span>
        </label>
        <label class="ml-3 cursor-pointer mt-0 pt-0">
          <input
            type="radio"
            name="send_weekly_report"
            id="send-weekly-report-no"
            bind:group={form.send_weekly_report}
            value={false}
            onclick={savePlanUserSettings}
          />
          <span>No</span>
        </label>
      </div>
    </div>
    <div class="col-span-3 sm:col-start-2 sm:col-span-2">
      <p class="text-gray-500 text-sm">
        Weekly progress reminder email and weekly activity page report.
      </p>
    </div>
  </div>

  {#if form.send_weekly_report}
    <div class="mt-6 grid grid-cols-3 gap-y-2 gap-x-4">
      <div
        class="col-span-2 sm:col-span-1 flex items-center justify-start sm:justify-end"
      >
        <label for="email" class="mt-0 pt-0 text-sm font-medium text-gray-700">
          Weekly Activity Report Format: {form.send_weekly_report}
        </label>
      </div>
      <div class="flex items-center justify-start col-span-3 sm:col-span-2">
        <div class="max-w-xl flex rounded-md shadow-sm">
          <select
            name="weekly_report_format"
            class="w-40"
            bind:value={form.weekly_report_format}
            onchange={savePlanUserSettings}
          >
            <option value="full">Full Report</option>
            <option value="summary">Summary Report</option>
          </select>
        </div>
      </div>
      <div class="col-span-3 sm:col-start-2 sm:col-span-2">
        <p class="text-gray-500 text-sm">
          The full report includes a snapshot of the full Activity page, the
          summary report only includes highlights.
        </p>
      </div>
    </div>
  {/if}
{/if}

<div class="separator"></div>

<h2
  class="mt-6 text-lg font-semibold leading-7 text-gray-900 flex justify-between items-center"
>
  Global Email Notification Settings
</h2>

<div class="mt-6 grid grid-cols-3 gap-y-2 gap-x-4">
  <div
    class="col-span-2 sm:col-span-1 flex items-center justify-start sm:justify-end"
  >
    <label for="email" class="mt-0 pt-0 text-sm font-medium text-gray-700">
      Send Notification Summary?
    </label>
  </div>
  <div class="flex items-center justify-start col-span-3 sm:col-span-2">
    <div class="max-w-lg flex items-center">
      <label class="cursor-pointer mt-0 pt-0">
        <input
          type="radio"
          name="send_notification_summary"
          id="send-notification-summary-yes"
          bind:group={form.send_notification_summary}
          value={true}
          onchange={saveGlobalEmailSettings}
        />
        <span>Yes</span>
      </label>
      <label class="ml-3 cursor-pointer mt-0 pt-0">
        <input
          type="radio"
          name="send_notification_summary"
          id="send-notification-summary-no"
          bind:group={form.send_notification_summary}
          value={false}
          onchange={saveGlobalEmailSettings}
        />
        <span>No</span>
      </label>
    </div>
  </div>
  <div class="col-span-3 sm:col-start-2 sm:col-span-2">
    <p class="text-gray-500 text-sm">
      Summary of un-read notifications from the previous day.
    </p>
  </div>
</div>

<div class="separator"></div>

<h2 class="text-lg font-semibold leading-7 text-gray-900 mt-8">
  Mobile App Push Notification Settings
</h2>
<p class="mt-1 max-w-2xl text-sm text-gray-500">
  Please choose whether you want to be sent push notifications. To recieve them
  you also need to download the Lead With Purpose mobile app and enable push
  notifications on your mobile device. <a
    class="text-blue-600 hover:underline hover:text-blue-700"
    href="https://apps.apple.com/us/app/lead-with-purpose/id1600163569"
    target="_blank">Download from iOS App Store</a
  >
  OR
  <a
    class="text-blue-600 hover:underline hover:text-blue-700"
    href="https://play.google.com/store/apps/details?id=com.leadwithpurpose.lwpMobile"
    target="_blank"
  >
    Download from Google Play Store
  </a>
</p>
<div class="mt-6 grid grid-cols-3 gap-y-2 gap-x-4">
  <div
    class="col-span-2 sm:col-span-1 flex items-center justify-start sm:justify-end"
  >
    <label for="email" class="mt-0 pt-0 text-sm font-medium text-gray-700">
      Send Push Notifications?
    </label>
  </div>
  <div class="flex items-center justify-start col-span-3 sm:col-span-2">
    <div class="max-w-lg flex items-center">
      <label class="cursor-pointer mt-0 pt-0">
        <input
          type="radio"
          name="send_push_notifications"
          id="send-push-notifications-yes"
          bind:group={form.send_push_notifications}
          value={true}
          onchange={saveGlobalEmailSettings}
        />
        <span>Yes</span>
      </label>
      <label class="ml-3 cursor-pointer mt-0 pt-0">
        <input
          type="radio"
          name="send_push_notifications"
          id="send-push-notifications-no"
          bind:group={form.send_push_notifications}
          value={false}
          onchange={saveGlobalEmailSettings}
        />
        <span>No</span>
      </label>
    </div>
  </div>
</div>

<style lang="postcss">
  .separator {
    --color-separator: #cdcdcd;
    margin-top: 1.5rem;
    background-color: var(--color-separator);
    height: 1px;
    width: 100%;
    box-shadow: 0 2px 4px 0 var(--color-separator);
  }
</style>
